import {Box, createStyles, Group, Stack, Text} from '@mantine/core';
import {css} from 'aphrodite';
import React, {useState} from 'react';

import {getExtensionIcon} from 'dev-platform/extensionIconMapper';
import McpUrlParamFields from 'nav/sidepanel/mcps/McpUrlParamFields';
import notifications, {Response} from 'notifications';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as net from 'util/Net';

import {Extension} from './extensionConstants';
import {EXTENSION_URL_BASE, extensionIconStyles} from './ExtensionDetails';

const useStyles = createStyles(() => ({
  headerTextStack: {
    flex: 1,
    minWidth: 0,
  },
}));

interface InstallConfigStepProps {
  extension: Extension;
  onCancel: () => void;
  onInstalled: () => void;
}

// Wizard step shown when installing an extension that requires URL params.
export default function InstallConfigStep({
  extension,
  onCancel,
  onInstalled,
}: InstallConfigStepProps): JSX.Element {
  const {classes} = useStyles();
  const [urlParamValues, setUrlParamValues] = useState<Record<string, string>>({});
  const [installing, setInstalling] = useState(false);

  const mcpProperties = extension.latestVersion?.mcpProperties ?? null;
  const urlParams = mcpProperties?.urlParams ?? [];
  const serverUrlTemplate = mcpProperties?.serverUrl ?? null;
  const displayName = extension.name ?? extension.id;
  const missingUrlParams = urlParams.some((p) => !(urlParamValues[p.key] ?? '').trim());

  const doInstall = async (): Promise<void> => {
    setInstalling(true);
    try {
      await net.postJSONPromise({
        url: `${EXTENSION_URL_BASE}/${extension.id}:install`,
        data: {extensionType: extension.extensionType, urlParamValues},
      });
      notifications.success(`${displayName} installed successfully.`);
      onInstalled();
    } catch (err: unknown) {
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err as Response);
      } else {
        throw err;
      }
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Stack spacing="lg" p="md">
      {/* Header: matches the layout of ExtensionDetails */}
      <Group align="flex-start" spacing="md">
        <Box className={css(extensionIconStyles.icon)}>
          {getExtensionIcon(extension.id, {
            className: css(extensionIconStyles.iconImg, extensionIconStyles.iconPlaceholder),
            extensionType: extension.extensionType,
          })}
        </Box>
        <Stack spacing="xs" className={classes.headerTextStack}>
          <Group spacing="xs" align="center" noWrap={true}>
            <Text fw={700} size="xl" component="span">
              {displayName}
            </Text>
            <Text size="sm" c="dimmed" component="span">
              by {extension.provider.name}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            To finish installing, fill in the connection details below.
          </Text>
        </Stack>
        <Group spacing="xs" noWrap={true}>
          <Button
            text="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            onClick={onCancel}
          />
          <Tooltip
            container="body"
            placement={TooltipPlacement.TOP}
            title="Fill in all required fields to install"
            disabled={!missingUrlParams}
          >
            <span aria-disabled={missingUrlParams ? 'true' : undefined}>
              <Button
                text="Install"
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.MEDIUM}
                disabled={installing || missingUrlParams}
                onClick={doInstall}
              />
            </span>
          </Tooltip>
        </Group>
      </Group>

      <Box mt="md">
        <McpUrlParamFields
          urlParams={urlParams}
          urlParamValues={urlParamValues}
          onChange={(key, value) => {
            setUrlParamValues((prev) => ({...prev, [key]: value}));
          }}
          serverUrlTemplate={serverUrlTemplate}
        />
      </Box>
    </Stack>
  );
}
